// One recording: the video (or the audio, when there is no video), the
// files, the notes and action items, and the transcript by speaker. Only the
// account that owns the room can open it (worker/src/account.ts
// recordingDetail). The media plays as soon as the recording is filed; the
// words follow a few minutes later, and the page picks them up on its own.

import { Fragment, useEffect, useState, type JSX, type ReactNode } from "react";
import type { RecordingDetail } from "@breeze/core";
import { api, ApiError } from "../lib/api";
import { goBack } from "../lib/nav";
import { ownRoomPath } from "../lib/storage";
import { callWhen, clock, duration } from "../lib/format";
import { BackIcon } from "../components/icons";

/** How often a recording that is still being transcribed is checked again. */
const REFRESH_MS = 20_000;

export function RecordingScreen({ id }: { readonly id: string }): JSX.Element {
  const [detail, setDetail] = useState<RecordingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = () => {
      api<RecordingDetail>(`/api/recordings/${encodeURIComponent(id)}`, { signal: controller.signal })
        .then((d) => {
          setDetail(d);
          setError(null);
          if (d.status === "processing" || d.status === "recording") timer = setTimeout(load, REFRESH_MS);
        })
        .catch((err: unknown) => {
          if ((err as Error).name === "AbortError") return;
          setError(err instanceof ApiError ? err.message : "Could not load the recording.");
        });
    };
    load();
    return () => {
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [id]);

  const video = detail?.files.find((f) => f.label === "Video");
  const audio = detail?.files.find((f) => f.label === "Audio");
  const playable = detail && detail.status !== "failed" && (video || audio);

  return (
    <div className="page">
      <div className="page__head">
        <a className="btn btn--ghost btn--icon" href={ownRoomPath()} onClick={(e) => { e.preventDefault(); goBack(); }} aria-label="Back" style={{ width: "var(--tap-min)", height: "var(--tap-min)", marginLeft: -10 }}>
          <BackIcon size={22} />
        </a>
        <h1 className="page__title" style={{ fontSize: 20 }}>
          {detail ? callWhen(detail.started) || "Recording" : "Recording"}
        </h1>
      </div>

      {error ? <div className="notice notice--danger">{error}</div> : null}
      {!detail && !error ? <div className="skeleton" style={{ height: 220 }} /> : null}

      {detail ? (
        <>
          {playable ? (
            video ? (
              <video src={video.url} controls playsInline preload="metadata" style={{ width: "100%", borderRadius: "var(--radius)", aspectRatio: "16 / 9" }} />
            ) : audio ? (
              <audio src={audio.url} controls preload="metadata" style={{ width: "100%" }} />
            ) : null
          ) : null}

          {detail.status === "failed" ? (
            <div className="notice notice--danger">This recording did not finish.</div>
          ) : detail.status !== "ready" ? (
            <div className="notice">
              {playable ? "The transcript and notes land here in a few minutes." : "Still processing. The video and transcript land here in a few minutes."}
            </div>
          ) : null}

          {playable ? (
            <div className="card__actions" style={{ alignItems: "center" }}>
              <span className="muted" style={{ flex: 1, fontSize: 14.5 }}>
                {duration(detail.durationSecs)}
              </span>
              {detail.files.map((f) => (
                <a key={f.url} className="btn" href={f.url} download>
                  {f.label === "Video" ? "Download video" : "Download audio"}
                </a>
              ))}
            </div>
          ) : null}

          {detail.summary ? (
            <section className="section">
              <h2 className="section__heading">Notes</h2>
              <div className="notes">{renderNotes(detail.summary)}</div>
            </section>
          ) : null}

          {detail.actionItems.length > 0 ? (
            <section className="section">
              <h2 className="section__heading">Action items</h2>
              <ul className="notes__list">
                {detail.actionItems.map((a, i) => (
                  <li key={i}>
                    {a.description}
                    {a.assignee ? <span className="muted"> ({a.assignee})</span> : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {detail.transcript.length > 0 ? (
            <section className="section">
              <h2 className="section__heading">Transcript</h2>
              <div className="transcript">
                {detail.transcript.map((line, i) => (
                  <div key={i} className="transcript__line">
                    <span className="transcript__time">{clock(line.start)}</span>
                    <span>
                      {i === 0 || detail.transcript[i - 1]!.speaker !== line.speaker ? (
                        <span className="transcript__speaker">{line.speaker} </span>
                      ) : null}
                      {line.text}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** The notes are markdown in one known shape (worker/src/summary.ts):
 * "## " and "### " headings, "- " bullets, and paragraphs, with **bold**
 * inline. Rendered as elements, never as HTML. */
export function renderNotes(markdown: string): ReactNode {
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];
  const flush = () => {
    if (!bullets.length) return;
    const items = bullets;
    bullets = [];
    blocks.push(
      <ul key={blocks.length} className="notes__list">
        {items.map((b, i) => (
          <li key={i}>{inline(b)}</li>
        ))}
      </ul>,
    );
  };
  for (const raw of markdown.split(/\r?\n/)) {
    const line = raw.trim();
    const bullet = /^[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      bullets.push(bullet[1]!);
      continue;
    }
    flush();
    if (!line) continue;
    if (line.startsWith("### ")) blocks.push(<h4 key={blocks.length} className="notes__topic">{inline(line.slice(4))}</h4>);
    else if (line.startsWith("## ")) blocks.push(<h3 key={blocks.length} className="notes__heading">{inline(line.slice(3))}</h3>);
    else if (line.startsWith("# ")) blocks.push(<h3 key={blocks.length} className="notes__heading">{inline(line.slice(2))}</h3>);
    else blocks.push(<p key={blocks.length} className="notes__para">{inline(line)}</p>);
  }
  flush();
  return blocks;
}

function inline(text: string): ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") && part.length > 4 ? <strong key={i}>{part.slice(2, -2)}</strong> : <Fragment key={i}>{part}</Fragment>,
  );
}
